// ----------------------------------------------------------------------
// <copyright file="Izenda.PropertiesEditorManager.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * PropertiesEditorManager
 */
!(function() {
  var m = require('mobx');
  var PERMISSION_KEY = require('logic/data/Izenda.AuthorizationConstants').PERMISSION_KEY;
  var SecurityManager = require('share/authorization/Izenda.SecurityManager');
  var securityManager = new SecurityManager();
  const {
    isArray,
    flow,
    keys,
    find,
    reduce,
    isString,
    isObject,
    uniqueId,
    forEach,
    includes,
    zipObject,
    isFunction,
    concat,
    get,
    getOr,
    invokeArgs,
    head
  } = require('lodash/fp');

  var DEFAULT_COMPONENT_NAME = 'defaultEditor';

  // save information of editor: name, schema, default action....
  var componentManager = {};

  // save status of each editor: is observerble object, allow to add action when ever its status changed (active/deactive)
  var showingEditors = m.observable({});

  // save information of registed-component: component, propeties...
  var showingComponents = {};

  var handlersInfo = {};

  // Default state
  var viewStyle = m.observable({
    simpleState: false
  });

  /**
   * Find editorId by names
   * @param  {array} editorNames
   * @return {string}
   */
  function findEditorIdByName(editorNames) {
    editorNames = isArray(editorNames) ? editorNames : [editorNames];

    var found = flow(keys, find(hasEditor))(componentManager);

    return found;

    function hasEditor(editorId) {
      var editorInfo = componentManager[editorId] || {};
      var names = editorInfo.editorNames || [];

      return reduce(
        function(re, editorName) {
          return re && includes(editorName, names);
        },
        true,
        editorNames
      );
    }
  }

  /**
   * Reset editors
   * @param {String | Object} editors
   */
  function resetEditor(editors) {
    editors = editors || DEFAULT_COMPONENT_NAME;

    var editorNames;

    if (isString(editors)) {
      editorNames = [editors];
    } else if (isObject(editors)) {
      editorNames = Object.keys(editors);
    }

    if (!editorNames) {
      throw new Error('Cannot add editor');
    }

    var editorId = findEditorIdByName(editorNames);

    componentManager[editorId] && delete componentManager[editorId];
    showingEditors[editorId] && delete showingEditors[editorId];
    showingComponents[editorId] && delete showingComponents[editorId];
    handlersInfo[editorId] && delete handlersInfo[editorId];
  }

  /**
   * Add editor
   * @param {object}  component       react-component
   * @param {object}  editors         editors information
   * @param {array}   defaultParams   used to build schema-data
   */
  function addEditor(component, editors, defaultParams) {
    editors = editors || DEFAULT_COMPONENT_NAME;

    var editorNames;

    if (isString(editors)) {
      editorNames = [editors];
    } else if (isObject(editors)) {
      editorNames = Object.keys(editors);
    }

    if (!editorNames) {
      throw new Error('Cannot add editor');
    }

    var newEditorId = uniqueId('editor_');

    componentManager[newEditorId] = {
      component: component,
      defaultParams: defaultParams,
      editorNames: editorNames
    };

    // init status for each editor in showingEditors: default status is disabled
    showingComponents[newEditorId] = {};
    showingEditors[newEditorId] = {};
    handlersInfo[newEditorId] = {};

    forEach(function(name) {
      m.extendObservable(showingEditors[newEditorId], zipObject([name], [false]));
    }, editorNames);

    component.setEditorInfo &&
      component.setEditorInfo({
        handlers: handlersInfo[newEditorId]
      });

    return isObject(editors) ? getDefaultSchema(editors, defaultParams) : null;

    function getDefaultSchema(editors, defaultParams) {
      var defaultSchemas = {};
      var editorName, definedEditor;

      for (editorName in editors) {
        definedEditor = editors[editorName];
        if (isFunction(definedEditor.defaultSchema)) {
          defaultSchemas[editorName] = getSchema(newEditorId, definedEditor.defaultSchema);
        }
      }

      return defaultSchemas;
    }
  }

  /**
   * Build schema data
   * @param  {String} editorId
   * @param  {Function} definedSchema
   * @param  {Array} params
   * @return {Object}
   */
  function getSchema(editorId, definedSchema, params) {
    var componentInfo = componentManager[editorId];
    var editorComp = componentInfo.component;
    var defaultParams = getOr([], 'defaultParams', componentInfo);

    return definedSchema.apply(null, concat([editorComp], concat(defaultParams, params)));
  }

  /**
   * Rerender prop-editor with new config
   * @param  {[type]} editorComp [description]
   * @return {[type]}            [description]
   */
  function renderEditor(editorComp, editorId, activeEditorName, registerComponent, registerProps, propsEditors) {
    if (!editorComp || !editorComp.forceUpdate) {
      return;
    }

    invokeArgs('beforeRender', editorComp, [registerComponent]);

    editorComp.forceUpdate && editorComp.forceUpdate();
  }

  /**
   * Regist to to show defined-schema in added-editor
   * @param  {Object} registerComponent react-component
   * @param  {Object} registerProps
   * @param  {Object} propsEditors
   * @return {Any}
   */
  function registerByEditors(registerComponent, registerProps, propsEditors) {
    if (!propsEditors) {
      return;
    }

    var args = Array.prototype.slice.call(arguments, 3);
    var usingEditors = getEditorInfo(propsEditors);
    var editorId = findEditorIdByName(usingEditors.names);

    if (!editorId) {
      throw new Error('Cannot not render, using editors are not added');
    }

    var newActiveEditor = usingEditors.active;
    var showingEditor = showingEditors[editorId];
    var showingComponent = get(newActiveEditor, showingComponents[editorId]);

    // new regist-editor is active
    if (showingEditor[newActiveEditor]) {
      // registerProps is same as current-active-editor
      if (registerProps === get('props', showingComponent)) {
        return;
      }

      if (
        !registerProps.isRerenderOnActive &&
        get('getDefinedSchema', usingEditors.activeInfo) === get('propsEditors.getDefinedSchema', showingComponent)
      ) {
        return;
      }
    }

    var editorComp = get('component', componentManager[editorId]);

    // 1. get schema def to each-editor
    propsEditors.forEach(function(editor) {
      editor.schemaDef =
        editor.getDefinedSchema && getSchema(editorId, editor.getDefinedSchema, concat(registerProps, args));
    });

    if (!editorComp) {
      throw new Errow('Cannot render');
    }

    // 2. save status of registed-component & update status of editor and update default view mode
    for (var name in showingEditors[editorId]) {
      showingEditors[editorId][name] = false;
    }

    showingEditors[editorId][newActiveEditor] = true;
    showingComponents[editorId][newActiveEditor] = {
      component: registerComponent,
      props: registerProps,
      propsEditors: usingEditors.activeInfo
    };

    viewStyle.simpleState = !securityManager.checkPrivileges([PERMISSION_KEY.ReportsDataSourcesAdvancedDataSources]);

    // 3. update config info to editor-component
    editorComp.setPropertySchemas && editorComp.setPropertySchemas(editorId, propsEditors, usingEditors.active);

    // 4.re-render properties editor for new schema
    renderEditor(editorComp, editorId, usingEditors.active, registerComponent, registerProps, propsEditors);
  }

  /**
   * Get editor information
   * @param  {Array} propsEditors
   * @return {Object}
   */
  function getEditorInfo(propsEditors) {
    var info = {
      names: [],
      active: '',
      activeInfo: null
    };

    forEach(function(editor) {
      info.names.push(editor.name);

      if (editor.isActive) {
        info.active = editor.name;
        info.activeInfo = editor;
      }
    }, propsEditors);

    // acitve editor is not default, first editor will be active as default
    if (!info.active) {
      info.activeInfo = head(propsEditors);
      info.active = get('name', info.activeInfo);
    }

    return info;
  }

  /**
   * Set active editor: in-case added-editor is tab-list (tab change will be change acitve-editor)
   * @param {[type]} editorName [description]
   */
  function setActiveEditor(editorName) {
    var editorId = findEditorIdByName(editorName);
    if (editorId) {
      for (var name in showingEditors[editorId]) {
        showingEditors[editorId][name] = false;
      }

      showingEditors[editorId][editorName] = true;

      // set default editor here
      viewStyle.simpleState = !securityManager.checkPrivileges([PERMISSION_KEY.ReportsDataSourcesAdvancedDataSources]);
      return true;
    }

    return false;
  }

  /**
   * Check whethers editor is active or not
   * @param  {Object}  editors
   * @return {Boolean}
   */
  function isShowingPropsEditor(editors) {
    var editorInfo = getEditorInfo(editors);

    var editorId = findEditorIdByName(editorInfo.names);

    if (editorId && showingEditors[editorId][editorInfo.active]) {
      return true;
    }

    return false;
  }

  /**
   *  Get component info for active editors
   * @param  {Object} editors
   * @return {Object}
   */
  function getShowingComponentInfo(editors) {
    var isShowingEditor = isShowingPropsEditor(editors);

    var editorInfo;
    var editorId;

    if (isShowingEditor) {
      editorInfo = getEditorInfo(editors);
      editorId = findEditorIdByName(editorInfo.active);

      return editorId && get(editorInfo.active, showingComponents[editorId]);
    }

    return null;
  }

  /**
   * Check whether active other editor
   * @param  {Object} editors
   * @return {Promise}
   */
  function canSwitch(editors) {
    var editorInfo = getEditorInfo(editors);
    var editorId = findEditorIdByName(editorInfo.active);

    var editorComp = get('component', componentManager[editorId]);

    // check validation
    var validation = get('component.props.validation', componentManager[editorId]) || {};

    if (isFunction(validation.validate)) {
      return validation.validate();
    }

    return Promise.resolve(true);
  }

  /**
   * Get editor status
   * @param  {Object} editors
   * @return {Object}
   */
  function getEditorStatus(editors) {
    var editorInfo = getEditorInfo(editors);
    var editorId = findEditorIdByName(editorInfo.active);

    return editorId && getEditorInfoById(editorId);
  }

  /**
   * Get editor info by editor-id
   * @param  {Object} editorId
   * @return {Object}
   */
  function getEditorInfoById(editorId) {
    return {
      info: componentManager[editorId],
      status: showingEditors[editorId],
      registedComponent: showingComponents[editorId]
    };
  }

  /**
   * Find editor Id
   * @param  {Object} editors
   * @return {String}
   */
  function findEditorId(editors) {
    var editorInfo = getEditorInfo(editors);
    return findEditorIdByName(editorInfo.active);
  }

  function addHandlers(editorId, handlerName, handlers) {
    var newHandlers = {};
    newHandlers[handlerName] = handlers;

    $izenda.extend(handlersInfo[editorId], newHandlers);
  }

  function takeAction(editorId, actionName, editorName, editorInfo) {
    var args = Array.prototype.slice.call(arguments, 4);

    invokeArgs(actionName, args, editorInfo);
    invokeArgs(editorName, args, get(actionName, handlersInfo[editorId]));
  }

  function getViewStyle() {
    return viewStyle;
  }

  function switchViewMode() {
    viewStyle.simpleState = !viewStyle.simpleState;
  }

  module.exports = {
    addEditor: m.action(addEditor),
    resetEditor: m.action(resetEditor),
    registerByEditors: m.action(registerByEditors),
    setActiveEditor: m.action(setActiveEditor),
    addHandlers: addHandlers,

    isShowingPropsEditor: isShowingPropsEditor,
    canSwitch: canSwitch,

    getShowingComponentInfo: getShowingComponentInfo,
    getEditorStatus: getEditorStatus,
    getEditorInfoById: getEditorInfoById,
    findEditorId: findEditorId,
    findEditorIdByName: findEditorIdByName,
    takeAction: m.action(takeAction),
    // Control simple or advance view on properties editor
    getViewStyle: getViewStyle,
    switchViewMode: switchViewMode
  };
})();



// WEBPACK FOOTER //
// ./ui/modules/Izenda.PropertiesEditorManager.js